import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';
import dayjs from 'dayjs';
import { OrgReportsApi, OrgStudentAttendanceReportRow } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgStudentAttendanceReportRow>();

const BASE_COLUMNS = [
  columnHelper.accessor('studentName', { header: 'Name' }),
  columnHelper.accessor('schoolName', { header: 'School' }),
  columnHelper.accessor('className', { header: 'Class' }),
  columnHelper.accessor('workingDays', { header: 'Working Days' }),
  columnHelper.accessor('totalPresent', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('totalAbsent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('percentage', { header: '%', cell: (ctx) => `${ctx.getValue()}%` }),
];

/**
 * Student Attendance Report — org tier, read-only. Flat per-student
 * working_days/present/absent/percentage over a date range, matching the
 * real `state-reports.student-attendance-report` shape — cross-school,
 * not a per-school aggregate (see the sibling Students Attendance Summary
 * report for that one). Defaults to month-to-date, same as real legacy.
 */
export function OrgStudentAttendanceReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...BASE_COLUMNS,
  ];

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, classId, startDate, endDate]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-student-attendance-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-student-attendance-report-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setClassId('');
  }

  const filterParams = {
    schoolId: schoolId || undefined,
    classId: (schoolId && classId) || undefined,
    startDate,
    endDate,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-student-attendance-report', dt.params, filterParams],
    queryFn: () => OrgReportsApi.studentAttendanceReport({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportStudentAttendanceReport(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Student Attendance Report"
        description="Per-student working days, present, absent, and attendance percentage, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more students than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by class'}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No students found."
        getRowId={(row) => `${row.schoolId}:${row.studentId}`}
      />
    </>
  );
}
